import { LocalDate, DateTimeFormatter } from "@js-joda/core";

import {
    declareAiCapability,
    provideAiCapability,
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";

// Visit instances describing its own filter bar. The page opens on today, so a request that names no
// dates keeps today rather than quietly widening to every visit the agency has ever had.
declareAiCapability({
    id: "visitInstances.filter",
    title: "Filter the visit instances list",
    effect: "prefill",
    description:
        "Sets the filters on Visits > Visit instances and re-runs its search: the visit date range, contract, a patient or caregiver name, their display ids, a visit id and an invoice number. The page opens on today. Only works while that page is open.",
    location: "the filter bar of the Visits > Visit instances page",
    scope: "page",
    permissionKey: "view_visits_section_instances",
    params: [
        { name: "from", type: "date", required: false, description: "First visit day to include, as yyyy-MM-dd." },
        { name: "to", type: "date", required: false, description: "Last visit day to include, as yyyy-MM-dd." },
        {
            name: "contract",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getVisitInstanceContractOptions(),
            description: "Limit to these contracts."
        },
        { name: "patient", type: "text", required: false, description: "A patient name to match." },
        { name: "caregiver", type: "text", required: false, description: "A caregiver name to match." },
        { name: "patientDisplayId", type: "text", required: false, description: "A patient's display id." },
        { name: "caregiverDisplayId", type: "text", required: false, description: "A caregiver's display id." },
        { name: "visitId", type: "text", required: false, description: "A single visit id." },
        { name: "invoiceId", type: "text", required: false, description: "An invoice number the visit is on." }
    ],
    examples: [
        "visit instances last week",
        "visits for patient smith yesterday",
        "visit instances on the france contract"
    ]
});

let visitInstanceContractTypes = () => [];

function getVisitInstanceContractOptions() {
    return visitInstanceContractTypes().map((contract) => ({ value: String(contract.id), label: contract.name }));
}

//! @ngInject
export function visitInstancesCtrl($scope, $uibModal, NgTableParams, itemSearchPageManager, toaster, DatabaseApi, $rootScope, SweetAlert) {

    const selectedVisitInstances = new Set();
    $scope.selectedVisitInstances = selectedVisitInstances;

    const selectedVisitInstancesModels = {};
    selectedVisitInstancesModels["allInPage"] = false;
    selectedVisitInstancesModels["allInTable"] = false;
    $scope.selectedVisitInstancesModels = selectedVisitInstancesModels;

    let items;

    $scope.tableParams = null;
    $scope.tableColumns = {
        VisitId: true,
        VisitDate: true,
        VisitTime: true,
        Patient: true,
        Caregiver: true,
        Location: true,
        Contract: true,
        ServiceCode: false,
        InvoiceDisplayId: false,
        ClockIn: false,
        ClockOut: false
    };

    async function initialize() {
        initPageManager();
        $scope.loadItems();
    }

    $scope.$on("refresh_visits", function () {
        $scope.loadItems();
    })

    var initPageManager = function () {
        $scope.pageManager = itemSearchPageManager.createSearchPageManager("/visit_instances");
        $scope.contractTypesDataManager = $scope.pageManager.getContractTypesDataManager();

        $scope.pageManager.initFromToDateParams();
        $scope.pageManager.updateSearchParamValue("from", new Date(LocalDate.now().format(DateTimeFormatter.ofPattern("MM/dd/yyyy"))));
        $scope.pageManager.updateSearchParamValue("to", new Date(LocalDate.now().format(DateTimeFormatter.ofPattern("MM/dd/yyyy"))));
        $scope.pageManager.initContractTypesParam();
        $scope.pageManager.initSearchParam("patient", "");
        $scope.pageManager.initSearchParam("caregiver", "");
        $scope.pageManager.initSearchParam("patientDisplayId", "");
        $scope.pageManager.initSearchParam("caregiverAgencyDisplayId", "");
        $scope.pageManager.initSearchParam("visitId", "");
        $scope.pageManager.initSearchParam("invoiceDisplay", "");
    };

    var initTable = function (items) {
        var options = {
            count: 25,
            sorting: { id: "desc" }
        };
        $scope.tableParams = new NgTableParams(options, {
            dataset: items
        });
    };


    visitInstanceContractTypes = () => $scope.contractTypesDataManager?.getActiveContractTypes() ?? [];

    const releaseVisitInstancesFilter = provideAiCapability("visitInstances.filter", (args) => {
        const applied = [];
        const asList = (value) => (Array.isArray(value) ? value : [value]);
        const asDate = (text) => {
            const parts = String(text).split("-").map(Number);

            if (parts.length !== 3 || parts.some(isNaN)) {
                throw new Error("Could not read the date " + text + ".");
            }

            return new Date(parts[0], parts[1] - 1, parts[2]);
        };
        const pickAll = (available, requested, what) =>
            asList(requested).map((one) => {
                const found = (available ?? []).find((item) => String(item.id) === String(one));

                if (found === undefined) {
                    throw new Error("This agency has no " + what + " " + one + ".");
                }

                return found;
            });

        if (args.from !== undefined) {
            $scope.pageManager.searchParams.from.value = asDate(args.from);
        }

        if (args.to !== undefined) {
            $scope.pageManager.searchParams.to.value = asDate(args.to);
        }

        if (args.from !== undefined || args.to !== undefined) {
            applied.push([args.from, args.to].filter(Boolean).join(" to "));
        }

        if (args.contract !== undefined) {
            const contracts = pickAll(visitInstanceContractTypes(), args.contract, "contract");

            $scope.pageManager.searchParams.selectedContractTypes.value = contracts;
            applied.push(contracts.map((contract) => contract.name).join(" and "));
        }

        for (const [arg, param, label] of [
            ["patient", "patient", "patient"],
            ["caregiver", "caregiver", "caregiver"],
            ["patientDisplayId", "patientDisplayId", "patient id"],
            ["caregiverDisplayId", "caregiverAgencyDisplayId", "caregiver id"],
            ["visitId", "visitId", "visit"],
            ["invoiceId", "invoiceDisplay", "invoice"]
        ]) {
            if (args[arg] !== undefined) {
                $scope.pageManager.searchParams[param].value = String(args[arg]);
                applied.push(label + " " + String(args[arg]));
            }
        }

        $scope.loadItems();

        return applied.length === 0
            ? "Re-ran the visit instances search"
            : "Showing visit instances: " + applied.join(", ");
    });

    $scope.$on("$destroy", releaseVisitInstancesFilter);

    $scope.loadItems = function () {
        $scope.pageManager.executeSearch().then(function (response) {

            items = response.data.visitInstances;
            initTable(items);
        }, function (error) {
            toaster.pop("error", "Failed to load visit instances");
        });
    };

    const deselectAllInTable = () => {
        for (const key of Object.keys(selectedVisitInstancesModels)) {
            selectedVisitInstancesModels[key] = false;    
        }
        selectedVisitInstances.clear();
    }

    $scope.handleClickCheckbox = (row) => {
        selectedVisitInstancesModels["allInPage"] = false;

        selectedVisitInstances.has(row.id)
            ? selectedVisitInstances.delete(row.id)
            : selectedVisitInstances.add(row.id);
    }

    $scope.handleClickEdit = () => {
        if (selectedVisitInstances.size === 1) {
            $rootScope.openVisitInstanceModal([...selectedVisitInstances][0]);
        } else {
            $scope.handleClickBulkEdit();
        }
    }

    $scope.handleClickBulkEdit = () => {
        let selectedIds = [...selectedVisitInstances];

        if (selectedVisitInstancesModels["allInTable"]) {
            selectedIds = [...items.map(x => x.id)];
        }

        $rootScope.openBulkVisitInstancesModal(selectedIds);
    }

    $scope.handleClickDelete = () => {
        const visitInstanceIdsArr = [...selectedVisitInstances];
        const modalInstance = $uibModal.open({
            templateUrl: "admin/views/approve-visit-instance-delete-modal.html",
            size: "md",
            controller: "approveVisitInstanceDeleteModalCtrl",
            resolve: {
                visitInstanceIds: () => visitInstanceIdsArr
            }
        });

        modalInstance.result.then(function (res) {
            if (res.success === true) {
                $rootScope.$broadcast('refresh_visits');
                initTable(items.filter(x => !visitInstanceIdsArr.includes(x.id)));
            }
        });
    }

    $scope.handleClickSelectAllInPage = () => {
        const isSelectedAll = !selectedVisitInstancesModels["allInPage"];
        deselectAllInTable();

        $scope.tableParams.data.forEach((row) => {
            if (isSelectedAll) {
                selectedVisitInstances.add(row.id);
                selectedVisitInstancesModels[row.id] = true;
            } else {
                selectedVisitInstances.delete(row.id);
                selectedVisitInstancesModels[row.id] = false;
            }
        });
    }

    $scope.handleClickSelectAllInTable = () => {
        for (const item of items) {
            selectedVisitInstancesModels[item.id] = true;
            selectedVisitInstances.add(item.id);
        }
    }

    initialize();
};